import type { AsyncMethodReturns, Connection } from "penpal";
import { connectToParent } from "penpal";
import type {
  MessengerConfig,
  UrlCacheableConfig,
} from "@deskpro-product/messenger-api-schemas/messenger/config";
import type {
  ChildApi,
  MessengerAppEvent,
  MessengerCustomEvent,
  ParentApi,
} from "@deskpro-product/messenger-api-schemas/messenger-init/types";
import type { MutableRefObject } from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getExpandedIframeDimensions, getIframeDimensions } from "../utils/getIframeDimensions";
import mergeConfigWithOverrides from "../utils/mergeConfigWithOverrides";
import type { BasePath } from "../../../[locale]/lib/types/routeTypes";
import useIsStandaloneApp from "@/lib/client/hooks/useIsStandaloneApp";
import launcherInit from "../utils/launcherInit";
import { useShallow } from "zustand/react/shallow";
import { useLauncherStore } from "./LauncherStoreContext";
import type { SdkConversationMessages } from "./launcherStore";
import {
  getOrUpdateSession,
  useSessionContext,
} from "../../../[locale]/lib/client/contexts/SessionContext";
import getMessengerConfig, { getMessengerToken } from "@/lib/client/utils/config";
import { useNavigate, useParams } from "@tanstack/react-router";
import getHost from "@/lib/client/utils/getHost";
import { MessengerApiClient } from "@/lib/client/utils/client";
import {
  useActiveConversation,
  useAuth,
  useDevice,
  useReset,
  useWasClosed,
} from "../../../[locale]/lib/client/contexts/DeviceContext";
import getHostUserJwtToken from "../../../[locale]/lib/client/utils/getHostUserJwtToken";
import getInitialPageForUser from "../../../[locale]/lib/client/utils/getInitialPageForUser";

export type LauncherContextType = {
  connection: MutableRefObject<Connection<ParentApi> | undefined>;
  api: MessengerApiClient;
};

export const LauncherContext = createContext<LauncherContextType | undefined>(undefined);
export const useLauncherContext = (): LauncherContextType => {
  const c = useContext(LauncherContext);
  if (!c) {
    throw new Error("useLauncherContext() can only be called within a LauncherProvider");
  }
  return c;
};

/**
 * Main entry point for all SDK methods.
 * LauncherContext provides the connection ref as a context value.
 * Any state should be added to launcherStore and accessed via useLauncherStore rather than added here
 */
export default function LauncherProvider({
  tenantId,
  messengerId,
  hostConfig,
  config: serverConfig,
  children,
  platform: serverPlatform,
  basePath,
  mediaBaseUrl,
}: {
  tenantId: string;
  messengerId: string;
  hostConfig: string;
  config: MessengerConfig;
  basePath: BasePath;
  mediaBaseUrl: string;
  children: React.ReactNode;
  platform: NonNullable<UrlCacheableConfig["platform"]>;
}) {
  const params = useParams({
    strict: false,
  });

  const messengerToken = getMessengerToken(hostConfig);
  const connection = useRef<Connection<ParentApi> | undefined>(undefined);
  const { device_id } = useDevice();
  const { auth, setAuth } = useAuth();
  const reset = useReset();

  const authorization = useRef<string | null>(auth ?? null);
  useEffect(() => {
    authorization.current = auth ?? null;
  }, [auth]);

  const api = useMemo(
    () =>
      new MessengerApiClient(
        tenantId,
        messengerId,
        messengerToken,
        connection,
        authorization,
        setAuth,
        reset,
      ),
    [tenantId, messengerId, messengerToken, connection, setAuth, reset],
  );
  const isStandaloneApp = useIsStandaloneApp();
  const { hostJwt, setHostJwt } = useSessionContext();
  const [isReady, setIsReady] = useState(false);
  const { wasClosed, setWasClosed } = useWasClosed();
  const { hasActiveConversation } = useActiveConversation();

  const {
    setIsInitialized,
    config,
    setConfig,
    setShowLauncherButton,
    setTheme,
    isOpen,
    setIsOpen,
    setGoTo,
    host,
    setHost,
    setIsFullscreen,
    setPlatform,
    conversationMessages,
    setConversationMessages,
    setBasePath,
    setTenantId,
    setMessengerId,
    setPushDeviceToken,
    setMediaBaseUrl,
    setOverrideLocale,
    setIsVisible,
  } = useLauncherStore(
    useShallow((state) => ({
      setIsInitialized: state.setIsInitialized,
      config: state.config,
      setConfig: state.setConfig,
      setShowLauncherButton: state.setShowLauncherButton,
      setTheme: state.setTheme,
      isOpen: state.isOpen,
      setIsOpen: state.setIsOpen,
      setGoTo: state.setGoTo,
      setIsFullscreen: state.setIsFullscreen,
      setPlatform: state.setPlatform,
      host: state.host,
      setHost: state.setHost,
      setAuthStatus: state.setAuthStatus,
      conversationMessages: state.sdkConversationMessages,
      setConversationMessages: state.setSdkConversationMessages,
      setBasePath: state.setBasePath,
      setTenantId: state.setTenantId,
      setMessengerId: state.setMessengerId,
      setPushDeviceToken: state.setPushDeviceToken,
      mediaBaseUrl: state.mediaBaseUrl,
      setMediaBaseUrl: state.setMediaBaseUrl,
      setOverrideLocale: state.setOverrideLocale,
      setIsVisible: state.setIsVisible,
    })),
  );

  useEffect(() => {
    setBasePath(basePath);
    setConfig(serverConfig);
    setPlatform(serverPlatform);
    setTenantId(tenantId);
    setMessengerId(messengerId);
    setMediaBaseUrl(mediaBaseUrl);
  }, [
    basePath,
    messengerId,
    serverConfig,
    serverPlatform,
    mediaBaseUrl,
    setBasePath,
    setConfig,
    setMessengerId,
    setPlatform,
    setTenantId,
    setMediaBaseUrl,
    tenantId,
  ]);

  const navigate = useNavigate();

  const dependencies = useRef({
    hostJwt,
    setHostJwt,
    reset,
  });

  if (hostJwt !== dependencies.current.hostJwt) {
    dependencies.current.hostJwt = hostJwt;
  }
  if (setHostJwt !== dependencies.current.setHostJwt) {
    dependencies.current.setHostJwt = setHostJwt;
  }
  if (reset !== dependencies.current.reset) {
    dependencies.current.reset = reset;
  }

  const methods: ChildApi = useMemo(
    () => ({
      async init(
        _messengerId,
        {
          showLauncherButton,
          launcherButtonConfig,
          messengerAppConfig,
          parentViewDimensions,
          open,
          goTo: _goTo,
          host,
          locale: overrideLocale,
        },
      ) {
        if (_messengerId !== messengerId) {
          return;
        }

        const config = mergeConfigWithOverrides(
          serverConfig,
          messengerAppConfig,
          launcherButtonConfig,
        );

        launcherInit(config, setConfig, setTheme);

        setHost(host ?? getHost(serverPlatform));
        setGoTo(_goTo);
        setOverrideLocale(overrideLocale);

        const offsetBottom =
          parentViewDimensions === "fullscreen"
            ? 0
            : config.settings?.displaySettings.placement?.offsetBottom || 0;

        const offsetSide =
          parentViewDimensions === "fullscreen"
            ? 0
            : config.settings?.displaySettings.placement?.offsetSide || 0;

        const side =
          config.settings?.displaySettings.placement?.position === "bottom-left" ? "left" : "right";

        const _isOpen = (hasActiveConversation && !wasClosed) || open || isOpen;
        setIsOpen(_isOpen);

        setShowLauncherButton(showLauncherButton);

        const { height, width, type } = getIframeDimensions({
          isOpen: _isOpen,
          showLauncherButton,
          offsetBottom,
          offsetSide,
          parentViewDimensions,
        });
        setIsFullscreen(type === "fullscreen");

        if (serverPlatform === "WEB") {
          const parentApi = await connection.current?.promise;
          await parentApi?.setIsOpen(messengerId, _isOpen);
        }

        setIsInitialized(true);

        return {
          side,
          offsetBottom,
          offsetSide,
          width,
          height,
        };
      },
      async open(
        _messengerId,
        { parentViewDimensions, showLauncherButton, goTo: _goTo, locale: overrideLocale },
      ) {
        if (_messengerId !== messengerId) {
          return;
        }

        const offsetBottom =
          parentViewDimensions === "fullscreen"
            ? 0
            : config?.settings?.displaySettings.placement?.offsetBottom || 0;

        const offsetSide =
          parentViewDimensions === "fullscreen"
            ? 0
            : config?.settings?.displaySettings.placement?.offsetSide || 0;

        setIsOpen(true);
        setShowLauncherButton(showLauncherButton);
        // Prevent the widget auto re-opening on refresh
        setWasClosed(false);

        if (_goTo || overrideLocale) {
          setGoTo(_goTo);
          setOverrideLocale(overrideLocale);
          navigate({
            to: "/$tenantId/$messengerId/$hostConfig",
            params: { tenantId, messengerId, hostConfig },
          });
        }

        const { height, width } = getIframeDimensions({
          isOpen: true,
          showLauncherButton,
          offsetBottom,
          offsetSide,
          parentViewDimensions,
        });

        setShowLauncherButton(showLauncherButton);

        if (serverPlatform === "WEB") {
          const parentApi = await connection.current?.promise;
          await parentApi?.setIsOpen(messengerId, true);
        }

        return {
          height,
          width,
        };
      },
      async close(_messengerId, { parentViewDimensions, showLauncherButton }) {
        if (_messengerId !== messengerId) {
          return;
        }

        setIsOpen(false);
        setShowLauncherButton(showLauncherButton);
        // Prevent the widget auto re-opening on refresh
        setWasClosed(true);

        const offsetBottom =
          parentViewDimensions === "fullscreen"
            ? 0
            : config?.settings?.displaySettings.placement?.offsetBottom || 0;

        const offsetSide =
          parentViewDimensions === "fullscreen"
            ? 0
            : config?.settings?.displaySettings.placement?.offsetSide || 0;

        const { height, width } = getIframeDimensions({
          isOpen: false,
          showLauncherButton,
          offsetBottom,
          offsetSide,
          parentViewDimensions,
        });

        if (serverPlatform === "WEB") {
          const parentApi = await connection.current?.promise;
          await parentApi?.setIsOpen(messengerId, false);
        }

        // Allow animation to complete before returning
        return new Promise<{ height: string; width: string }>((resolve) =>
          setTimeout(() => resolve({ height, width }), 300),
        );
      },
      async setUserInfo(_messengerId, _user) {
        if (_messengerId !== messengerId) {
          return false;
        }
        const a = authorization.current;
        if (!a) {
          return false;
        }
        const session = await getOrUpdateSession({
          tenantId,
          messengerId,
          messengerToken,
          auth: a,
        });
        if (!session) return false;
        if (session.userConfirmed.isConfirmed) return false;
        return false;
      },
      async setSignedUserInfo(_messengerId, { token }) {
        if (_messengerId !== messengerId) {
          return false;
        }
        dependencies.current.setHostJwt(token);
        return true;
      },
      async setDeviceToken(_messengerId, { token }) {
        if (_messengerId !== messengerId) {
          return false;
        }
        setPushDeviceToken(token);
        return true;
      },
      async sendNotification(_messengerId, notification) {
        if (_messengerId !== messengerId) {
          return false;
        }
        try {
          const result = await api.sendNotificationMutation(device_id, notification);
          return result.sent;
        } catch (e) {
          console.error("Error sending notification", e);
          return false;
        }
      },
      async getUnreadCount(_messengerId) {
        if (_messengerId !== messengerId) {
          return 0;
        }

        const conversations = await api.getConversations();

        return conversations.reduce((acc, { messages }) => {
          if (messages.unreadMessages) {
            acc += Object.keys(messages.unreadMessages).length;
          }
          return acc;
        }, 0);
      },
      async sendMessageAsUser(_messengerId, { conversationId, message }) {
        if (_messengerId !== messengerId) {
          return false;
        }

        const messages = conversationMessages?.[conversationId] ?? [];
        const updatedConversationMessages: SdkConversationMessages = {
          ...conversationMessages,
          [conversationId]: [...messages, { type: "text", text: message }],
        };
        setConversationMessages(updatedConversationMessages);

        return true;
      },
      async logout(_messengerId) {
        if (_messengerId !== messengerId) {
          return false;
        }
        if (dependencies.current.hostJwt) {
          dependencies.current.setHostJwt(null);
        } else {
          dependencies.current.reset();
        }
        await navigate(
          getInitialPageForUser({
            tenantId,
            messengerId,
            hostConfig,
            locale: (params as { locale: string }).locale || "en-US",
            config,
            lastVisitedTab: undefined,
            activeConversations: [],
          }),
        );
        return true;
      },
      async refreshConfig(_messengerId, messengerToken) {
        if (_messengerId !== messengerId) {
          return;
        }
        const { config: mergedConfig, mediaBaseUrl } = await getMessengerConfig({
          tenantId,
          messengerId,
          platform: serverPlatform,
          host,
          messengerToken,
        });

        setMediaBaseUrl(mediaBaseUrl);
        launcherInit(mergedConfig, setConfig, setTheme);

        return true;
      },
      onResize(
        _messengerId,
        {
          size: parentViewDimensions,
          showLauncherButton,
          isOpen,
          isExpanded,
          isCompactExpanded,
          proactiveChatDimensions,
        },
      ) {
        if (_messengerId !== messengerId) {
          return;
        }

        const offsetBottom =
          parentViewDimensions === "fullscreen"
            ? 0
            : config?.settings?.displaySettings.placement?.offsetBottom || 0;

        const offsetSide =
          parentViewDimensions === "fullscreen"
            ? 0
            : config?.settings?.displaySettings.placement?.offsetSide || 0;

        const { height, width, type } = getExpandedIframeDimensions({
          isOpen,
          isExpanded,
          isCompactExpanded,
          showLauncherButton,
          offsetBottom,
          offsetSide,
          parentViewDimensions,
          proactiveChatDimensions,
        });
        setIsFullscreen(type === "fullscreen");

        return {
          height,
          width,
        };
      },
      async setVisibility(_messengerId, { isVisible }) {
        if (_messengerId !== messengerId) {
          return false;
        }

        console.log(`[${messengerId}] isVisible:`, isVisible);
        setIsVisible(isVisible);
        return true;
      },
    }),
    [
      messengerId,
      serverConfig,
      setConfig,
      setTheme,
      setHost,
      serverPlatform,
      setGoTo,
      setOverrideLocale,
      hasActiveConversation,
      wasClosed,
      isOpen,
      setIsOpen,
      setShowLauncherButton,
      setIsFullscreen,
      setIsInitialized,
      config,
      setWasClosed,
      navigate,
      tenantId,
      hostConfig,
      messengerToken,
      setPushDeviceToken,
      api,
      device_id,
      conversationMessages,
      setConversationMessages,
      params,
      host,
      setMediaBaseUrl,
      setIsVisible,
    ],
  );

  useEffect(() => {
    /**
     * There are 3 modes the app can run in:
     * - standalone app: opening messenger app directly in the browser, no host page or host app, only for development
     * - mobile app: opening messenger app as a webview in a mobile app. window?.DESKPRO_MESSENGER_CONNECTION will be set by the mobile app
     * - web app: opening messenger app as an iframe another a web page. Penpal.connectToParent will connect to the parent page
     */
    if (isStandaloneApp) {
      console.log("--------isStandaloneApp---------");
      connection.current = {
        promise: new Promise<AsyncMethodReturns<ParentApi>>((resolve) => {
          resolve({
            ready: async () => {
              // If you hit the app directly there is no host page so the init methods will never get called.
              methods.init(messengerId, {
                showLauncherButton: true,
                open: true,
                parentViewDimensions: "fullscreen",
              });
            },
            open: async () => {},
            close: async () => {},
            expand: async () => {},
            collapse: async () => {},
            toggleProactiveChat: async () => {},
            getViewDimensions: async () => ({
              height: window.innerHeight,
              width: window.innerWidth,
            }),
            getSignedUserInfo: async () => undefined,
            appEvent: async (messengerId: string, event: MessengerAppEvent) => {
              console.log("appEvent", { messengerId, event });
            },
            customEvent: async (messengerId: string, event: MessengerCustomEvent) => {
              console.log("customEvent", { messengerId, event });
            },
            webPushShowNotificationsPanel: async () => true,
            webPushSubscribe: async () => null,
            webPushUnsubscribe: async () => true,
            setIsOpen: async () => {},
          });
        }),
        destroy: () => {},
      };
      // Make the methods available to window for testing.
      window.DpMessengerStandaloneApi = methods;
    } else if (window?.DpMessengerConnection) {
      console.log("--------isMobileApp---------");
      connection.current = {
        promise: new Promise<AsyncMethodReturns<ParentApi>>((resolve) => {
          if (window.DpMessengerConnection) {
            resolve(window.DpMessengerConnection.parentMethods);
          }
        }),
        destroy: () => {},
      };
      window.DpMessengerConnection.childMethods = methods;
    } else {
      console.log("--------isWebApp---------");
      console.log("connecting to parent");
      connection.current = connectToParent<ParentApi>({
        methods,
      });
    }

    connection.current.promise.then(async (parent) => {
      console.log("created parent connection");
      // Only run parent.ready on mount
      if (!isReady) {
        const hostUserJwtToken = await getHostUserJwtToken(messengerId, connection.current);
        setHostJwt(hostUserJwtToken || null);
        await parent.ready(messengerId);
        setIsReady(true);
      }
    });

    return () => {
      console.log("destroying parent connection");
      return connection.current?.destroy();
    };
    // The only time we want to run this effect is when the app is first mounted
    // In general we try not to rely on reading state values in the `methods` object because this requires us to add it as a dep here which then will destroy and recreate the connection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ connection, api }), [connection, api]);
  return <LauncherContext.Provider value={value}>{children}</LauncherContext.Provider>;
}
